import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthContext.jsx';
import { useApiError } from '../hooks/useApiError.js';
import { homeForRole } from '../layouts/navigation.js';
import LanguageSwitcher from '../components/LanguageSwitcher.jsx';
import { Field, TextInput } from '../components/Field.jsx';

/**
 * One button per role, so every part of the app is one click away.
 * Keep this in step with the roles in backend/src/config/roles.js - an account
 * that exists but is not listed here is effectively invisible in the demo.
 *
 * DEVELOPMENT ONLY. This block used to render unconditionally, which printed
 * the shared demo password in clear text on the login screen of every
 * deployment - telling any visitor the convention the accounts were created
 * with. `import.meta.env.DEV` is false under `vite build`, so the constant and
 * the markup below are dropped from the production bundle entirely rather than
 * merely hidden.
 */
const DEMO_ACCOUNTS = [
  { email: 'lea@crm.local', role: 'TELEPRO' },
  { email: 'sarah@crm.local', role: 'COMMERCIAL' },
  { email: 'paris@crm.local', role: 'AGENCY' },
  { email: 'it@crm.local', role: 'IT' },
  { email: 'admin@crm.local', role: 'ADMIN' },
];

const LoginPage = () => {
  const { t } = useTranslation();
  const { signIn, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toMessage = useApiError();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  if (isAuthenticated) {
    return <Navigate to={location.state?.from?.pathname ?? homeForRole(user.role)} replace />;
  }

  const onSubmit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const signedIn = await signIn(email, password);
      navigate(location.state?.from?.pathname ?? homeForRole(signedIn.role), { replace: true });
    } catch (err) {
      setError(err.status === 401 ? t('auth.invalidCredentials') : toMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const useDemo = (demoEmail) => {
    setEmail(demoEmail);
    setPassword('Password123!');
  };

  return (
    <div className="auth-shell">
      <div className="auth-shell__aside">
        <p className="auth-shell__logo">CRM</p>
        <h1 className="auth-shell__headline">{t('auth.subtitle')}</h1>
      </div>

      <div className="auth-shell__main">
        <div className="auth-card">
          <div className="auth-card__top">
            <h2>{t('auth.title')}</h2>
            <LanguageSwitcher compact />
          </div>

          <form onSubmit={onSubmit} className="auth-form">
            <Field label={t('auth.email')} htmlFor="email" required>
              <TextInput
                id="email"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="username"
                required
                autoFocus
              />
            </Field>

            <Field label={t('auth.password')} htmlFor="password" required error={error}>
              <TextInput
                id="password"
                type="password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                required
              />
            </Field>

            <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
              {busy ? t('auth.signingIn') : t('auth.signIn')}
            </button>
          </form>

          {import.meta.env.DEV ? (
            <div className="auth-card__demo">
              <p className="auth-card__demo-title">{t('auth.demoAccounts')}</p>
              <div className="auth-card__demo-list">
                {DEMO_ACCOUNTS.map((account) => (
                  <button key={account.email} type="button" className="chip" onClick={() => useDemo(account.email)}>
                    {t(`roles.${account.role}`)}
                  </button>
                ))}
              </div>
              <p className="auth-card__demo-hint">Password123!</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
